/**
 * RolesPage — 系统设置「角色与权限」二级页：展示后端固定 4 角色闭集（root/admin/operator/viewer）
 * 的只读说明卡，点击角色卡打开 RoleEditor 查看其职责范围（无角色 CRUD，不支持自定义角色）。
 */

import { useState } from 'react';
import { Icons } from '~/lib/icons';
import { RoleEditor, type RoleItem } from './dialogs/RoleEditor';
import { NOT_IN_SCOPE_TITLE, SectionHead } from './settings-shared';

// ---------- Roles（4 角色闭集只读说明）----------
const ROLES: RoleItem[] = [
  {
    id: 'root',
    name: 'Root',
    color: 'var(--accent)',
    desc: '全权 + 账号 / 白名单 / BONUS',
    scope: '',
  },
  { id: 'admin', name: 'Admin', color: 'var(--info)', desc: '全权（不含 root 专属）', scope: '' },
  {
    id: 'operator',
    name: '运营',
    color: 'var(--chart-4)',
    desc: '内容 / 推送 / 反馈 / Banner / FAQ',
    scope: '',
  },
  {
    id: 'viewer',
    name: '只读',
    color: 'var(--chart-6)',
    desc: '只读所有列表 + 详情 · 无写权限',
    scope: '',
  },
];

export default function RolesPage() {
  const [editing, setEditing] = useState<{ role?: RoleItem } | null>(null);
  return (
    <>
      <SectionHead
        title="角色与权限"
        sub="固定 4 角色闭集 · root / admin / operator / viewer · 不支持自定义角色"
        action={
          // 新建角色：后端无角色 CRUD（4 角色闭集写死）→ disabled（§五）
          <button
            className="btn"
            disabled
            title={`${NOT_IN_SCOPE_TITLE} · RBAC 为 4 角色闭集，不支持自定义角色`}
            style={{ opacity: 0.55, cursor: 'not-allowed' }}
          >
            <Icons.IconLock size={13} />
            新建角色
          </button>
        }
      />
      <div
        className="hstack"
        style={{
          gap: 8,
          padding: 12,
          background: 'var(--bg-subtle)',
          borderRadius: 6,
          fontSize: 11.5,
          color: 'var(--fg-muted)',
          lineHeight: 1.55,
        }}
      >
        <Icons.IconInfo size={13} style={{ color: 'var(--info)', flexShrink: 0, marginTop: 1 }} />
        系统采用后端固定的 4 角色权限模型，无法新建 /
        删除角色或自定义权限矩阵。点击角色卡查看其职责范围；在「团队成员」处可把成员设为这 4
        个角色之一。
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 10,
        }}
      >
        {ROLES.map((r) => (
          <div
            key={r.id}
            className="card"
            style={{ padding: 12, cursor: 'pointer' }}
            onClick={() => setEditing({ role: r })}
          >
            <div className="hstack" style={{ gap: 8, marginBottom: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: r.color }} />
              <span style={{ fontWeight: 500, fontSize: 13 }}>{r.name}</span>
              <Icons.IconLock size={10} style={{ color: 'var(--fg-faint)' }} />
            </div>
            <div
              style={{ fontSize: 10.5, color: 'var(--fg-muted)', lineHeight: 1.5, minHeight: 30 }}
            >
              {r.desc}
            </div>
          </div>
        ))}
      </div>
      {editing && <RoleEditor role={editing.role} onClose={() => setEditing(null)} />}
    </>
  );
}
